<script setup lang="ts">
import LegendLib from "@/utils/LegendLib";
import { legendColors } from "./edit-legend.models";
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: string | number[] | undefined;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const emits = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();
const visible = ref(false);

const selectColor = (color: [number, number, number]) => {
  emits("update:modelValue", color.join(","));
  visible.value = false;
};

const hoveredColor = ref<[number, number, number] | null>(null);

const showHover = (color: [number, number, number]) => {
  hoveredColor.value = color;
};
const hideHover = () => {
  hoveredColor.value = null;
};

const currentColor = computed(() => {
  return LegendLib.toHex(props.modelValue);
});

const hoveredHex = computed(() =>
  hoveredColor.value ? LegendLib.rgbToHex(hoveredColor.value) : ""
);

const contrastedColor = computed<"black" | "white">(() => {
  let rgb: [number, number, number] | undefined;
  if (Array.isArray(props.modelValue)) {
    rgb = props.modelValue as [number, number, number];
  } else if (typeof props.modelValue === "string") {
    rgb = LegendLib.toRGB(props.modelValue);
  }
  if (!rgb) return "black";
  const brightness = Math.round(
    (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000
  );
  return brightness > 125 ? "black" : "white";
});
</script>

<template>
  <el-popover
    v-model:visible="visible"
    placement="bottom"
    :disabled="props.disabled"
    :width="560"
    trigger="click"
  >
    <template #reference>
      <button
        :disable="props.disabled"
        :class="{ disabled: props.disabled }"
        class="main-button"
        :style="{ backgroundColor: currentColor }"
      >
        <i
          v-if="!props.disabled"
          class="bi bi-chevron-down"
          :style="{ color: contrastedColor }"
        ></i>
      </button>
    </template>

    <!-- bootstrap class to show children on same line, 1 line  -->
    <div class="p-0 m-0 d-flex flex-row flex-wrap">
      <div
        v-for="(colorLine, i) in legendColors"
        :key="i"
        class="d-flex flex-column"
      >
        <button
          v-for="(color, j) in colorLine"
          :key="i + '-' + j"
          class="color-button"
          :style="{ backgroundColor: LegendLib.rgbToHex(color) }"
          @click="selectColor(color)"
          @mouseenter="showHover(color)"
          @mouseleave="hideHover"
        ></button>
      </div>
    </div>
    <!-- hover info -->
    <div class="hover-info" v-if="hoveredColor">
      <div class="swatch" :style="{ backgroundColor: hoveredHex }"></div>
      <div class="label">
        {{ hoveredColor ? hoveredColor.join(", ") : "" }}
        <small v-if="hoveredHex" class="hex">({{ hoveredHex }})</small>
      </div>
    </div>
  </el-popover>
</template>

<style lang="scss" scoped>
.main-button {
  outline: 1px solid #ccc;
  border: none;
  padding: 0;
  height: 20px;
  width: 20px;
  cursor: pointer;
  // center content
  display: flex;
  justify-content: center;
  align-items: center;
}
.color-button {
  outline: 1px solid #ccc;
  margin-top: 1px;
  margin-left: 1px;
  border-radius: 2px;
  border: none;
  padding: 0;
  height: 20px;
  width: 20px;
  cursor: pointer;
  // center content
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    border: 2px solid #000;
    transform: scale(1.1);
  }
}

.disabled {
  cursor: not-allowed !important;
}
</style>
